// Libraries
import { useEffect, useRef, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";

// Types
import { IGeoPoint } from "@/Settings/types/administrationTools/model";

// Helpers
import {
	calculatePositionTooltip,
	distanceBetweenTwoPoints,
	getCenterPolygon,
} from "@/Settings/helpers/AdministrationTools/Provider/globalPositioningService";

// Constants
import { POLYGON_VALUES } from "@/Settings/Constants/AdministrationTools/AdministrationTools";

type TLatLngDataProps = {
	lat: number;
	lng: number;
};

function LatLngData(props: Readonly<TLatLngDataProps>) {
	const { lat, lng } = props;

	return (
		<>
			<Typography className="!font-sans !font-normal !text-[12px] text-white">Lat: {lat}</Typography>
			<Typography className="!font-sans !font-normal !text-[12px] text-white">Long: {lng}</Typography>
		</>
	);
}

type TDrawPolygonProps = {
	_id: string;
	points: IGeoPoint[];
};

export default function DrawPolygon(props: Readonly<TDrawPolygonProps>) {
	const { _id, points } = props;

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [scaledPoints, setScaledPoints] = useState<{ x: number; y: number }[]>([]);

	useEffect(() => {
		if (points.length && canvasRef?.current) {
			const ctx = canvasRef.current.getContext("2d");

			if (ctx) {
				drawPolygon(ctx);
			}
		}
	}, [points]);

	const drawPolygon = (ctx: CanvasRenderingContext2D) => {
		if (!canvasRef?.current) {
			return;
		}

		ctx.clearRect(0, 0, POLYGON_VALUES.CANVAS.WIDTH, POLYGON_VALUES.CANVAS.HEIGHT);

		// Calculate the center of the polygon
		const centerPolygon = getCenterPolygon(points);

		// Calculate the maximum distance from the center
		const maxDistance = Math.max(
			...points.map((point) =>
				distanceBetweenTwoPoints({
					coordinateA: centerPolygon,
					coordinateB: point,
				})
			)
		);

		// Project points
		const projectedPoints = points.map((point) => {
			const dx = distanceBetweenTwoPoints({
				coordinateA: centerPolygon,
				coordinateB: { lat: centerPolygon.lat, lng: point.lng },
			});
			const dy = distanceBetweenTwoPoints({
				coordinateA: centerPolygon,
				coordinateB: { lat: point.lat, lng: centerPolygon.lng },
			});
			return {
				x: point.lng > centerPolygon.lng ? dx : -dx,
				y: point.lat > centerPolygon.lat ? -dy : dy, // Invert y-axis
			};
		});

		// Scale points to fit canvas
		const scale =
			(Math.min(POLYGON_VALUES.CANVAS.WIDTH, POLYGON_VALUES.CANVAS.HEIGHT) / (maxDistance * 2)) * POLYGON_VALUES.SCALE_VALUE;
		const newScaledPoints = projectedPoints.map((p) => ({
			x: p.x * scale + POLYGON_VALUES.CANVAS.WIDTH / 2,
			y: p.y * scale + POLYGON_VALUES.CANVAS.HEIGHT / 2,
		}));

		// Draw polygon
		ctx.beginPath();
		newScaledPoints.forEach((point, index) => {
			if (index === 0) {
				ctx.moveTo(point.x, point.y);
			} else {
				ctx.lineTo(point.x, point.y);
			}
		});

		ctx.closePath();
		ctx.fillStyle = POLYGON_VALUES.COLOR.fill;
		ctx.fill();
		ctx.strokeStyle = POLYGON_VALUES.COLOR.stroke;
		ctx.lineWidth = 1.5;
		ctx.stroke();

		// Draw dots
		newScaledPoints.forEach(({ x, y }) => {
			ctx.beginPath();
			ctx.arc(x, y, POLYGON_VALUES.RADIUS_DOT, 0, 2 * Math.PI);
			ctx.fillStyle = POLYGON_VALUES.COLOR.stroke;
			ctx.fill();
		});

		setScaledPoints(newScaledPoints);
	};

	return (
		<Box className="w-full max-w-md mx-auto relative">
			<canvas ref={canvasRef} height={150} width={130} />
			{points.map((coord, index) => {
				const point = scaledPoints[index];
				if (!point) {
					return null;
				}
				const key = `tooltip-dot-${_id}-${index}`;
				return (
					<Tooltip title={<LatLngData lat={coord.lat} lng={coord.lng} />} key={key} arrow>
						<Box
							className="absolute w-2 h-2 bg-transparent rounded-full cursor-pointer group"
							style={calculatePositionTooltip(point, {
								width: canvasRef.current?.width,
								height: canvasRef.current?.height,
							})}
						></Box>
					</Tooltip>
				);
			})}
		</Box>
	);
}
